/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import produce from 'immer';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/store/helpers';
import {gridDefaultPageSize} from '@ion/applications/src/utils/helpers';
import {getLocalStorage} from '@ion/core/src/capabilities/storage';
import {containsDelimiters, processDelimiters} from '@ion/core/src/utils/StringUtil';
import {USER_NAME_VERIFICATION_KEY} from '../actions/initialization';
import {
  ADD_TAB,
  CANCEL_DEVICE,
  CANCEL_JOB,
  CANCEL_TASK,
  DISPLAY_RERUN_JOB_DIALOG,
  INITIALIZE_JOBS,
  JOB_SEARCH_ENGINE_SHOW_ADD_DEVICES,
  REFRESH_GRID,
  REMOVE_TAB,
  RESET_NAME_FILTER,
  RESET_TABS,
  SET_ACTIVE_TAB,
  SET_GRID_COLUMNS,
  SET_GRID_PAGE_SIZE,
  SET_JOB_TAB_SEARCH,
  SET_JOBID_DEVICES_FILTER,
  SET_TAB_PAGE_SIZE,
  SET_USERNAME_FILTER,
  SET_VIEW_SUB_GROUPS,
  TOGGLE_FACETS_STATE,
  UPDATE_GRID_COLUMNS
} from '../actions/jobsearchengine';

export const PREFERENCE = 'jobsearchengine';

const getIntialTabs = () => {
  const username = getLocalStorage(USER_NAME_VERIFICATION_KEY);
  return [
    {
      key        : 'my-jobs',
      label      : 'My Jobs',
      nameFilter : '',
      filter     : `(username:(${username}))`,
      filters    : [{field: 'username', keyword: username}]
    },
    {
      key        : 'jobs',
      label      : 'All Jobs',
      nameFilter : '',
      filter     : '(jobId:*)',
      filters    : null
    }
  ];
};

const initialState = {
  activeTab : 'my-jobs',
  tabs      : getIntialTabs(),
  grid      : {
    columnState         : [],
    preferedColumnState : null,
    hasUpdate           : false,
    refreshRequired     : false,
    includeSubGroups    : false,
    pageSize            : gridDefaultPageSize
  },
  cancel : {
    isEditing : false,
    selected  : null
  },
  cancelTask : {
    isEditing    : false,
    taskSelected : null
  },
  cancelDevice : {
    isEditing      : false,
    deviceSelected : null,
    selected       : null
  },
  rerun : {
    isEditing : false,
    selected  : null
  },
  isFacetOpen    : false,
  showAddDevices : false
};

const reducers = {
  [INITIALIZE_JOBS] : draft => {
    const preference = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState = preference;
    draft.grid.preferedColumnState = preference;
  },
  [RESET_TABS] : draft => {
    draft.tabs = getIntialTabs();
  },
  [SET_ACTIVE_TAB] : (draft, {tabKey}) => {
    draft.activeTab = tabKey;
  },
  [ADD_TAB] : (draft, {tab}) => {
    draft.tabs.push(tab);
  },
  [REMOVE_TAB] : (draft, {tabKey}) => {
    draft.tabs = draft.tabs.filter((tab) => tab.key !== tabKey);
  },
  [SET_GRID_COLUMNS] : ({grid}, {columnState = []}) => {
    onSetGridColumns({
      grid,
      isLoading  : false,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}, {columnState = []}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [SET_GRID_PAGE_SIZE] : (draft, {pageSize}) => {
    draft.grid.pageSize = pageSize;
  },
  [SET_TAB_PAGE_SIZE] : (draft, {pageSize}) => {
    draft.grid.pageSize = pageSize;
  },
  [REFRESH_GRID] : (draft) => {
    draft.grid.refreshRequired = !draft.grid.refreshRequired;
  },
  [SET_VIEW_SUB_GROUPS] : (draft, {viewSubGroups}) => {
    draft.grid.includeSubGroups = viewSubGroups;
  },
  [TOGGLE_FACETS_STATE] : (draft) => {
    draft.isFacetOpen = !draft.isFacetOpen;
  },
  [JOB_SEARCH_ENGINE_SHOW_ADD_DEVICES] : (draft, {showAddDevices}) => {
    draft.showAddDevices = showAddDevices;
  },
  [SET_JOBID_DEVICES_FILTER] : (draft, {devices = []}) => {
    const currentTab = draft.tabs.find((tab) => tab.key === draft.activeTab);
    if (currentTab) {
      currentTab.devicesFilter = devices;
    }
  },
  [SET_USERNAME_FILTER] : (draft, {username = ''}) => {
    const myJobsTab = draft.tabs.find((tab) => tab.key === 'my-jobs');
    if (myJobsTab) {
      myJobsTab.filter = `(username:(${username}))`;
    }
  },
  [RESET_NAME_FILTER] : (draft) => {
    const currentTab = draft.tabs.find((tab) => tab.key === draft.activeTab);
    if (currentTab) {
      currentTab.nameFilter = '';
    }
  },
  [SET_JOB_TAB_SEARCH] : ({activeTab, tabs}, {filters}) => {
    const currentTab = tabs.find((tab) => tab.key === activeTab);
    if (currentTab) {
      let query = '';
      const isJobs = activeTab === 'jobs';
      const filterCount = filters?.length;
      if (filterCount) {
        filters.forEach(({field, keyword}) => {
          if (field === 'name') {
            currentTab.nameFilter = keyword;
            if (isJobs && filterCount === 1) {
              query = '(jobId:*)';
            }
          } else {
            query += createJobSearchQuery(query, field, keyword);
          }
        });
      } else if (isJobs) {
        query = '(jobId:*)';
      }
      currentTab.filters = filters;
      currentTab.filter = query;
    }
  },
  [CANCEL_JOB] : ({cancel}, {job, show}) => {
    cancel.selected  = show ? job : null;
    cancel.isEditing = show;
  },
  [CANCEL_TASK] : ({cancelTask}, {job, show}) => {
    cancelTask.taskSelected = show ? job : null;
    cancelTask.isEditing    = show;
  },
  [CANCEL_DEVICE] : ({cancelDevice}, {job, devicestatus, show}) => {
    cancelDevice.deviceSelected = show ? devicestatus : null;
    cancelDevice.selected       = show ? job : null;
    cancelDevice.isEditing      = show;
  },
  [DISPLAY_RERUN_JOB_DIALOG] : ({rerun}, {job, show}) => {
    rerun.selected  = show ? job : null;
    rerun.isEditing = show;
  }
};

export default produce((draft = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});

const createJobSearchQuery = (query, field, keyword) => {
  if ((field === 'jobId' || field === 'progressStatus') && containsDelimiters(keyword, ',', ' ')) {
    keyword = processDelimiters(keyword, {isExact: true, trim: true});
  } else if (field === 'username' && containsDelimiters(keyword, ',')) {
    keyword = processDelimiters(keyword, {isExact: true, trim: true, wrapEach: true, delimiters: [',']});
  }
  return `${query === '' ? '' : ' AND'} (${field}:${field === 'username' ? `(${keyword})` : keyword})`;
};
